import { createUseStyles } from 'react-jss';
import { Theme } from '@mui/material';

export const useStyles = createUseStyles((theme: Theme) => {
    const { color, radius, mq, motion, shadow } = theme.custom;
    return {
        root: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            borderRadius: radius.md,
            border: `1px solid ${color.border}`,
            backgroundColor: color.surface,
            overflow: 'hidden',
            transitionProperty: 'border-color, box-shadow',
            transitionDuration: motion.fast,
            '&:hover': { borderColor: color.borderStrong, boxShadow: shadow.sm },
            '&:hover $actions, &:focus-within $actions': { opacity: 1, pointerEvents: 'auto' },
            [mq.touch]: { '& $actions': { opacity: 1, pointerEvents: 'auto' } },
        },
        // The ring sits over the image (an inset shadow on the root would be hidden by the opaque media).
        test: {
            '& $media::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                boxShadow: `inset 0 0 0 2px ${color.tone.danger.main}`,
                pointerEvents: 'none',
            },
        },
        media: { position: 'relative', aspectRatio: '1', backgroundColor: color.surfaceMuted, overflow: 'hidden' },
        image: { display: 'block', width: '100%', height: '100%', objectFit: 'contain' },
        placeholder: {
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: 12,
            textAlign: 'center',
            fontSize: 12,
            color: color.text.secondary,
            fontVariantNumeric: 'tabular-nums',
        },
        badges: { position: 'absolute', top: 8, left: 8, right: 40, display: 'flex', flexWrap: 'wrap', gap: 4, pointerEvents: 'none' },
        info: {
            position: 'absolute',
            top: 6,
            right: 6,
            width: 28,
            height: 28,
            padding: 0,
            backgroundColor: color.overlay,
            color: color.tooltip.text,
            '&:hover': { backgroundColor: color.tooltip.bg },
        },
        actions: {
            position: 'absolute',
            right: 8,
            bottom: 8,
            display: 'flex',
            gap: 6,
            opacity: 0,
            pointerEvents: 'none',
            transitionProperty: 'opacity',
            transitionDuration: motion.fast,
        },
        tags: { display: 'flex', flexWrap: 'wrap', gap: 4, padding: 8, borderTop: `1px solid ${color.border}` },
        tag: {
            display: 'inline-block',
            maxWidth: '100%',
            padding: '2px 7px',
            borderRadius: radius.xs,
            fontSize: 11,
            lineHeight: '16px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        tagVisible: { backgroundColor: color.tone.success.soft, color: color.tone.success.onSoft },
        tagHidden: { backgroundColor: color.surfaceMuted, color: color.text.secondary, border: `1px dashed ${color.borderStrong}`, padding: '1px 6px' },
        tagMore: {
            backgroundColor: color.primary.soft,
            color: color.primary.onSoft,
            cursor: 'pointer',
            border: 'none',
            fontFamily: 'inherit',
            '&:focus-visible': { outline: `2px solid ${color.focusRing}` },
        },
        tagsEmpty: { padding: 8, borderTop: `1px solid ${color.border}`, fontSize: 11, color: color.text.muted },
    };
});
